import { createTheme } from "@mui/material/styles";

// One family. Inter is loaded in index.html with metric-adjacent system
// fallbacks so a slow font response reflows as little as possible.
const FONT_STACK = [
  '"Inter"',
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  '"Helvetica Neue"',
  "Arial",
  "sans-serif",
].join(", ");

/**
 * Type scale. Every role is defined once, here; pages must not invent
 * font sizes. Display and page/section headings are fluid so long titles keep
 * their proportions from 320px up to 1536px without breakpoint jumps.
 */
const typeScale = {
  // The hero headline is a sentence now ("Backend engineer building AI
  // systems"), not a two-word greeting, so the top of the ramp comes down.
  // At the old 4.5rem it ran to four lines on a laptop and read as shouting.
  display: "clamp(2.25rem, 1.5rem + 3.1vw, 3.625rem)",
  h1: "clamp(2rem, 1.45rem + 2.4vw, 3.25rem)",
  h2: "clamp(1.625rem, 1.3rem + 1.4vw, 2.25rem)",
  h3: "1.125rem",
  body: "1.0625rem",
  bodySm: "0.9375rem",
  caption: "0.8125rem",
};

/** Comfortable reading measure for long-form prose. */
const MEASURE = "68ch";

/** Bullet lists are short lines, not continuous prose, so they take more width. */
const MEASURE_WIDE = "84ch";

/**
 * Semantic design tokens, exposed as `theme.tokens`.
 *
 * Components read these instead of branching on `theme.palette.mode` inline,
 * which is what the whole codebase used to do: the same six colour ternaries
 * were copy-pasted into every card, chip and button.
 */
const tokens = {
  light: {
    surface: "#ffffff",
    // Paper grain, tiled on the body. The texture is a neutral 247 grey with
    // no colour cast, so it darkens the page very slightly without putting a
    // tint on a palette that is deliberately monochrome.
    // Spread onto any surface that should read as paper. The tile is 512px
    // native, drawn at 256 so it stays crisp on a 2x display.
    paper: {
      backgroundImage: "url(/paper-grain.webp)",
      backgroundSize: "256px",
      backgroundBlendMode: "multiply",
    },
    sectionGradient: "linear-gradient(135deg, #ffffff, #f0f0f0)",
    cardGradient: "linear-gradient(135deg, #ffffff, #f0f0f0)",
    cardShadow: "0 1px 4px rgba(0, 0, 0, 0.08), 0 10px 20px rgba(0, 0, 0, 0.06)",
    cardShadowHover:
      "0 2px 8px rgba(0, 0, 0, 0.10), 0 18px 36px rgba(0, 0, 0, 0.10)",
    hairline: "1px solid rgba(0, 0, 0, 0.12)",
    tag: {
      color: "rgba(8, 8, 8, 0.7)",
      background: "rgba(248, 248, 248, 0.6)",
      border: "1px solid rgba(207, 207, 207, 0.6)",
      hoverBorderColor: "rgba(0, 0, 0, 0.4)",
    },
    // High-contrast "inverted" button used for primary actions.
    invert: {
      background: "#1e1e1e",
      color: "#f0f0f0",
      hoverBackground: "#f5f5f5",
      hoverColor: "#000000",
      shadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    },
    sheen:
      "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)",
  },
  dark: {
    surface: "#000000",
    // No texture in dark mode. The grain is a near-white 247 grey, so
    // multiply leaves nothing visible and soft-light lifts the page from
    // rgb(10,10,10) to rgb(34,34,34) while still flattening the grain to
    // zero. Paper is a light-mode metaphor; a dark grain would need its own
    // inverted asset, which is not worth 15KB for an effect this quiet.
    paper: {
      backgroundImage: "none",
      backgroundSize: "auto",
      backgroundBlendMode: "normal",
    },
    sectionGradient: "linear-gradient(105deg, #050505, #1f1e1e)",
    cardGradient: "linear-gradient(125deg, #070707, #111111)",
    cardShadow: "0 1px 4px rgba(0, 0, 0, 0.6)",
    cardShadowHover: "0 18px 36px rgba(0, 0, 0, 0.7)",
    hairline: "1px solid rgba(255, 255, 255, 0.12)",
    tag: {
      color: "#f0f0f0",
      background: "rgba(255, 255, 255, 0.04)",
      border: "1px solid rgba(255, 255, 255, 0.18)",
      hoverBorderColor: "rgba(255, 255, 255, 0.5)",
    },
    invert: {
      background: "#ffffff",
      color: "#1e1e1e",
      hoverBackground: "#333333",
      hoverColor: "#ffffff",
      shadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
    },
    sheen:
      "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)",
  },
};

const palettes = {
  light: {
    background: { default: "#ffffff", paper: "#ffffff" },
    text: { primary: "rgba(0, 0, 0, 0.87)", secondary: "rgba(0, 0, 0, 0.6)" },
    divider: "rgba(0, 0, 0, 0.12)",
  },
  dark: {
    background: { default: "#0a0a0a", paper: "#000000" },
    text: {
      primary: "rgba(255, 255, 255, 0.92)",
      secondary: "rgba(255, 255, 255, 0.62)",
    },
    divider: "rgba(255, 255, 255, 0.14)",
  },
};

/**
 * @param {"light" | "dark"} mode
 */
export const createAppTheme = (mode) => {
  const t = tokens[mode];
  const dark = mode === "dark";

  // Light text on a dark surface reads thinner and tighter than the same text
  // on white, so give it a little more leading and tracking to compensate.
  const proseLineHeight = dark ? 1.78 : 1.72;
  const proseTracking = dark ? "0.006em" : "0";

  return createTheme({
    tokens: { ...t, measure: MEASURE, measureWide: MEASURE_WIDE, scale: typeScale },
    palette: { mode, ...palettes[mode] },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: FONT_STACK,
      // Hero voice, used once per page at most.
      display: {
        fontSize: typeScale.display,
        fontWeight: 700,
        lineHeight: 1.02,
        letterSpacing: "-0.035em",
      },
      h1: {
        fontSize: typeScale.h1,
        fontWeight: 700,
        lineHeight: 1.08,
        letterSpacing: "-0.03em",
      },
      h2: {
        fontSize: typeScale.h2,
        fontWeight: 700,
        lineHeight: 1.18,
        letterSpacing: "-0.02em",
      },
      h3: {
        fontSize: typeScale.h3,
        fontWeight: 700,
        lineHeight: 1.35,
        letterSpacing: "-0.01em",
      },
      h4: { fontSize: "1rem", fontWeight: 600, lineHeight: 1.4 },
      h5: { fontSize: "1rem", fontWeight: 600, lineHeight: 1.4 },
      h6: { fontSize: "1rem", fontWeight: 600, lineHeight: 1.4 },
      subtitle1: { fontSize: typeScale.body, fontWeight: 500, lineHeight: 1.6 },
      body1: {
        fontSize: typeScale.body,
        lineHeight: proseLineHeight,
        letterSpacing: proseTracking,
      },
      body2: {
        fontSize: typeScale.bodySm,
        lineHeight: proseLineHeight,
        letterSpacing: proseTracking,
      },
      caption: { fontSize: typeScale.caption, lineHeight: 1.5 },
      button: { textTransform: "none", fontWeight: 500, fontSize: "0.875rem" },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { scrollBehavior: "smooth", colorScheme: mode },
          body: { overflowX: "hidden", ...t.paper },
          // Respect the OS "reduce motion" setting everywhere at once.
          "@media (prefers-reduced-motion: reduce)": {
            "html:focus-within": { scrollBehavior: "auto" },
            "*, *::before, *::after": {
              animationDuration: "0.01ms !important",
              animationIterationCount: "1 !important",
              transitionDuration: "0.01ms !important",
              scrollBehavior: "auto !important",
            },
          },
          // Visible keyboard focus, invisible on mouse click.
          ":focus-visible": {
            outline: `2px solid ${mode === "dark" ? "#90caf9" : "#1976d2"}`,
            outlineOffset: "2px",
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: "none" } },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8, paddingInline: 20 },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { transition: "color 0.2s ease, background-color 0.2s ease" },
        },
      },
      MuiLink: { defaultProps: { underline: "none" } },
      MuiTypography: {
        defaultProps: {
          // Without this the custom `display` variant would render as a <span>.
          variantMapping: { display: "h1" },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 400 },
        },
      },
      MuiContainer: {
        styleOverrides: { root: { paddingInline: 16 } },
      },
    },
  });
};

export default createAppTheme;
